"use client";

import { Box, Group, Modal as MantineModal } from "@mantine/core";
import { useState } from "react";
import { FaStar } from "react-icons/fa";
import { FiExternalLink, FiMessageCircle } from "react-icons/fi";

import { Button, Checkbox, Modal, Stack, Text } from "@/components/ui/elements";
import {
  AppStoreButton,
  GooglePlayStoreButton,
} from "@/components/ui/parts/appStore";

import { useCloseOnHistoryBack } from "@/hooks/historyBack/useCloseOnHistoryBack";

import {
  detectAppReviewStorePlatform,
  getAppReviewStoreUrl,
  markAppReviewRequestDialogDismissed,
} from "./_function/appReviewRequestDialog";
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
} from "./_function/nativeAppReleaseConstants";

type Props = {
  opened: boolean;
  workerId: string | undefined;
  /** いずれの操作で閉じた場合も呼ばれる（既存の後続遷移を続行する） */
  onClose: () => void;
};

const STAR_COUNT = 5;

export const APP_REVIEW_REQUEST_BODY = [
  "勤務お疲れさまでした！",
  "いつもケアリンクをご利用いただきありがとうございます。",
  "ぜひ、アプリのレビューにご協力いただけますと幸いです。",
].join("\n");

export function AppReviewRequestDialog({ opened, workerId, onClose }: Props) {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const platform = detectAppReviewStorePlatform(
    typeof navigator === "undefined" ? "" : navigator.userAgent,
  );

  const finish = (shouldDismiss: boolean) => {
    if (shouldDismiss) markAppReviewRequestDialogDismissed(workerId);
    setDontShowAgain(false);
    onClose();
  };

  // 端末・ブラウザの戻る操作も「閉じる」と同じく、チェック状態に従う（C4）
  const closeWithHistory = useCloseOnHistoryBack(opened, () =>
    finish(dontShowAgain),
  );

  // ストアを開けたかどうかに関係なく「レビューする」押下として扱う
  const handleReview = () => closeWithHistory(() => finish(true));
  const handleClose = () => closeWithHistory(() => finish(dontShowAgain));

  return (
    <Modal
      opened={opened}
      close={handleClose}
      centered
      size="sm"
      padding={0}
      withCloseButton={false}
      styles={{
        content: { borderRadius: 16, overflow: "hidden" },
        body: { padding: 0 },
      }}
      zIndex={400}
    >
      <Stack
        gap={8}
        align="center"
        px="md"
        pt="lg"
        pb="sm"
        style={{
          background:
            "linear-gradient(135deg, var(--mantine-color-teal-0) 0%, var(--mantine-color-cyan-0) 100%)",
          borderBottom: "1px solid var(--mantine-color-teal-1)",
        }}
      >
        <Box
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          <FiMessageCircle
            size={28}
            color="var(--mantine-color-cyan-7)"
            aria-hidden
          />
        </Box>
        <Group gap={3} aria-hidden>
          {Array.from({ length: STAR_COUNT }, (_, i) => (
            <FaStar key={i} size={14} color="var(--mantine-color-yellow-5)" />
          ))}
        </Group>
        {/* Modal.Title にするとダイアログの aria-labelledby に紐づく */}
        <MantineModal.Title fz={17} fw={700} c="teal.8">
          アプリレビューのお願い
        </MantineModal.Title>
      </Stack>

      <Stack gap="sm" px="md" pt="sm" pb="md">
        <Text fz={13} lh={1.75} c="dark.6" style={{ whiteSpace: "pre-line" }}>
          {APP_REVIEW_REQUEST_BODY}
        </Text>

        <Stack gap={8}>
          {platform === "other" ? (
            <Stack gap={6}>
              <Group gap={8} justify="center" wrap="nowrap">
                <AppStoreButton
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  aria-label="App Storeでレビューする"
                  onClick={handleReview}
                />
                <GooglePlayStoreButton
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  aria-label="Google Playでレビューする"
                  onClick={handleReview}
                />
              </Group>
              <Text fz={11} c="gray.6" ta="center">
                お使いのスマートフォンのストアからレビューできます
              </Text>
            </Stack>
          ) : (
            <Button
              component="a"
              href={getAppReviewStoreUrl(platform)}
              target="_blank"
              rel="noopener noreferrer"
              color="teal"
              fullWidth
              radius="md"
              h={44}
              fz={15}
              leftSection={<FiExternalLink size={16} aria-hidden />}
              onClick={handleReview}
            >
              レビューする
            </Button>
          )}
          <Button
            variant="outline"
            color="gray"
            fullWidth
            radius="md"
            h={40}
            onClick={handleClose}
            styles={{
              root: {
                fontSize: 14,
                fontWeight: 600,
                backgroundColor: "#fff",
                color: "var(--mantine-color-dark-6)",
                borderColor: "var(--mantine-color-gray-4)",
              },
            }}
          >
            閉じる
          </Button>
        </Stack>

        <Checkbox
          label="今後表示しない"
          checked={dontShowAgain}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDontShowAgain(event.currentTarget.checked)
          }
          size="sm"
          styles={{
            root: { alignSelf: "center" },
            label: { color: "var(--mantine-color-dark-5)", fontSize: 13 },
          }}
        />
      </Stack>
    </Modal>
  );
}
